"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createVirtualDevices = exports.DeviceServer = void 0;
const lodash_1 = __importDefault(require("lodash"));
const random_normal_1 = __importDefault(require("random-normal"));
const uuid_1 = require("uuid");
const message_1 = require("./interfaces/message");
const reducer_1 = require("./reducer");
const util_1 = require("./util");
var DeviceClientLifecycle;
(function (DeviceClientLifecycle) {
    DeviceClientLifecycle[DeviceClientLifecycle["BeforeStart"] = 0] = "BeforeStart";
    DeviceClientLifecycle[DeviceClientLifecycle["Running"] = 1] = "Running";
    DeviceClientLifecycle[DeviceClientLifecycle["Stopped"] = 2] = "Stopped";
})(DeviceClientLifecycle || (DeviceClientLifecycle = {}));
function copyDevice(device) {
    return Object.assign({}, device);
}
class DeviceServer {
    constructor() {
        this.clients = [];
        this.devices = new Map();
        this.deviceIdByClient = new WeakMap();
        this.allowPoweredByClient = new WeakMap();
        this.randomnessByClient = new WeakMap();
    }
    onMessage(msg, client) {
        switch (msg.type) {
            case message_1.MessageType.CreateDeviceClient:
                const existingDeviceId = this.deviceIdByClient.get(client);
                if (existingDeviceId !== undefined &&
                    existingDeviceId !== msg.deviceId) {
                    console.warn("overwriting device");
                }
                this.deviceIdByClient.set(client, msg.deviceId);
                this.devices.set(msg.deviceId, {
                    id: msg.deviceId,
                    deviceClassKey: msg.deviceClassKey,
                    powerConsumption: 0,
                    powerConsumptionWithoutSavings: 0,
                    forAllUsers: msg.forAllUsers,
                });
                break;
            case message_1.MessageType.ReportDeviceClient:
                const deviceId = this.deviceIdByClient.get(client);
                if (deviceId === undefined) {
                    return;
                }
                const device = this.devices.get(deviceId);
                if (!device) {
                    return;
                }
                device.powerConsumption = msg.powerConsumption;
                device.powerConsumptionWithoutSavings =
                    msg.powerConsumptionWithoutSavings;
                break;
            default:
                return (0, util_1.unreachable)(msg);
        }
    }
    addClient(client) {
        this.clients.push(client);
        this.randomnessByClient.set(client, Math.random());
        client.start((msg) => this.onMessage(msg, client));
    }
    stop() {
        for (const client of this.clients) {
            client.stop();
        }
        this.clients = [];
        this.devices = new Map();
        this.deviceIdByClient = new WeakMap();
    }
    getDevices(limit) {
        const devices = [];
        for (const device of this.devices.values()) {
            devices.push(copyDevice(device));
            if (limit !== undefined && devices.length >= limit) {
                break;
            }
        }
        return devices;
    }
    setToggles(toggles) {
        var _a;
        const toggleByDeviceClassKey = new Map();
        for (const toggle of toggles) {
            toggleByDeviceClassKey.set(toggle.key, toggle);
        }
        for (const client of this.clients) {
            const deviceId = this.deviceIdByClient.get(client);
            if (deviceId === undefined) {
                continue;
            }
            const device = this.devices.get(deviceId);
            if (!device) {
                continue;
            }
            const toggle = toggleByDeviceClassKey.get(device.deviceClassKey);
            if (!toggle) {
                continue;
            }
            const allowPowered = ((_a = this.randomnessByClient.get(client)) !== null && _a !== void 0 ? _a : 0.5) > toggle.targetSavingRatio;
            if (allowPowered === this.allowPoweredByClient.get(client)) {
                continue;
            }
            this.allowPoweredByClient.set(client, allowPowered);
            client.onMessage({
                type: message_1.MessageType.PreferenceDeviceServer,
                allowPowered,
            });
        }
    }
}
exports.DeviceServer = DeviceServer;
class DeviceClient {
    constructor() {
        this.lifecycleState = DeviceClientLifecycle.BeforeStart;
    }
    start(sendMessage) {
        if (this.lifecycleState !== DeviceClientLifecycle.BeforeStart) {
            throw new Error("device has already been started");
        }
        this.sendMessage = sendMessage;
        this.runUntilStopped();
    }
    stop() {
        if (this.lifecycleState !== DeviceClientLifecycle.Running) {
            throw new Error("device is not running");
        }
        this.sendMessage = undefined;
        this.lifecycleState = DeviceClientLifecycle.Stopped;
    }
    runUntilStopped() {
        return __awaiter(this, void 0, void 0, function* () {
            while (!this.isStopped()) {
                try {
                    yield this.run();
                }
                catch (err) {
                    console.error("Device crashed", err);
                }
            }
        });
    }
    isStopped() {
        return this.lifecycleState === DeviceClientLifecycle.Stopped;
    }
    onButtonPressed() { }
}
class LegacyDeviceClient extends DeviceClient {
    constructor(deviceId, deviceClassKey, typicalPowerConsumption) {
        super();
        this.deviceId = deviceId;
        this.deviceClassKey = deviceClassKey;
        this.typicalPowerConsumption = typicalPowerConsumption;
        this.latestAllowPowered = true;
    }
    run() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            (_a = this.sendMessage) === null || _a === void 0 ? void 0 : _a.call(this, {
                type: message_1.MessageType.CreateDeviceClient,
                deviceId: this.deviceId,
                deviceClassKey: this.deviceClassKey,
                forAllUsers: false,
            });
            this.setPowered(true);
            while (!this.isStopped()) {
                yield (0, util_1.sleep)(5000);
            }
        });
    }
    onMessage(msg) {
        return __awaiter(this, void 0, void 0, function* () {
            this.latestAllowPowered = msg.allowPowered;
            yield (0, util_1.sleep)(Math.random() * 5000);
            this.setPowered(this.latestAllowPowered);
        });
    }
    setPowered(powered) {
        var _a;
        (_a = this.sendMessage) === null || _a === void 0 ? void 0 : _a.call(this, {
            type: message_1.MessageType.ReportDeviceClient,
            powerConsumption: powered ? this.typicalPowerConsumption : 0,
            powerConsumptionWithoutSavings: this.typicalPowerConsumption,
        });
    }
}
class SometimesOnDeviceClient extends DeviceClient {
    constructor(deviceId, deviceClassKey, typicalPowerConsumption, typicalOnTime, typicalOffTime, typicalResponseTime, autoOnOff, forAllUsers, onSetPowered) {
        super();
        this.deviceId = deviceId;
        this.deviceClassKey = deviceClassKey;
        this.typicalPowerConsumption = typicalPowerConsumption;
        this.typicalOnTime = typicalOnTime;
        this.typicalOffTime = typicalOffTime;
        this.typicalResponseTime = typicalResponseTime;
        this.autoOnOff = autoOnOff;
        this.forAllUsers = forAllUsers;
        this.onSetPowered = onSetPowered;
        this.latestAllowPowered = true;
        this.wantsToBePowered = false;
    }
    run() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            (_a = this.sendMessage) === null || _a === void 0 ? void 0 : _a.call(this, {
                type: message_1.MessageType.CreateDeviceClient,
                deviceId: this.deviceId,
                deviceClassKey: this.deviceClassKey,
                forAllUsers: this.forAllUsers,
            });
            if (!this.autoOnOff) {
                this.wantsToBePowered = false;
                this.autoSetPowered();
                while (!this.isStopped()) {
                    yield (0, util_1.sleep)(5000);
                }
                return;
            }
            const initiallyPowered = Math.random() <
                this.typicalOnTime / (this.typicalOnTime + this.typicalOffTime);
            this.wantsToBePowered = initiallyPowered;
            this.autoSetPowered();
            if (initiallyPowered) {
                yield (0, util_1.sleep)(Math.random() * this.noisyTime(this.typicalOnTime));
                this.wantsToBePowered = false;
                this.autoSetPowered();
            }
            else {
                yield (0, util_1.sleep)(Math.random() * this.noisyTime(this.typicalOffTime));
                this.wantsToBePowered = true;
                this.autoSetPowered();
                yield (0, util_1.sleep)(this.noisyTime(this.typicalOnTime));
                this.wantsToBePowered = false;
                this.autoSetPowered();
            }
            while (!this.isStopped()) {
                yield (0, util_1.sleep)(this.noisyTime(this.typicalOffTime));
                this.wantsToBePowered = true;
                this.autoSetPowered();
                yield (0, util_1.sleep)(this.noisyTime(this.typicalOnTime));
                this.wantsToBePowered = false;
                this.autoSetPowered();
            }
        });
    }
    onMessage(msg) {
        return __awaiter(this, void 0, void 0, function* () {
            this.latestAllowPowered = msg.allowPowered;
            yield (0, util_1.sleep)(Math.max(0, (0, random_normal_1.default)() + 1) * this.typicalResponseTime);
            this.autoSetPowered();
        });
    }
    setPowered(powered) {
        var _a;
        this.onSetPowered(powered);
        (_a = this.sendMessage) === null || _a === void 0 ? void 0 : _a.call(this, {
            type: message_1.MessageType.ReportDeviceClient,
            powerConsumption: powered ? this.typicalPowerConsumption : 0,
            powerConsumptionWithoutSavings: this.wantsToBePowered
                ? this.typicalPowerConsumption
                : 0,
        });
    }
    autoSetPowered() {
        this.setPowered(this.wantsToBePowered && this.latestAllowPowered);
    }
    noisyTime(time) {
        return Math.max(0, (0, random_normal_1.default)() * 0.5 + 1) * time;
    }
    onButtonPressed() {
        clearTimeout(this.latestButtonTimeout);
        this.latestButtonTimeout = undefined;
        if (this.wantsToBePowered) {
            this.wantsToBePowered = false;
            this.autoSetPowered();
        }
        else {
            this.wantsToBePowered = true;
            this.autoSetPowered();
            this.latestButtonTimeout = setTimeout(() => {
                this.wantsToBePowered = false;
                this.autoSetPowered();
            }, this.typicalOnTime);
        }
    }
}
function createVirtualDevices(deviceCount, onMicrowaveChange) {
    const settingsByDeviceClass = {
        fridge: { typicalOnTime: 10000, typicalOffTime: 40000 },
        microwave: { typicalOnTime: 45000, typicalOffTime: 400000 },
        oven: { typicalOnTime: 100000, typicalOffTime: 700000 },
        light: { typicalOnTime: 30000, typicalOffTime: 30000 },
        heater: { typicalOnTime: 800000, typicalOffTime: 600000 },
    };
    let realMicrowaveIndex;
    // TODO HACK need to scale this down to realistic values and scale up somewhere else
    const targetPower = reducer_1.gridMeanPower * (1 - reducer_1.nonSmartRatio);
    const deviceClients = lodash_1.default.times(deviceCount, (i) => {
        var _a;
        const deviceClass = reducer_1.allDeviceClassKeysSorted[Math.floor(Math.random() * reducer_1.allDeviceClassKeysSorted.length)];
        if (deviceClass === "microwave" && realMicrowaveIndex === undefined) {
            realMicrowaveIndex = i;
        }
        const settings = (_a = settingsByDeviceClass[deviceClass]) !== null && _a !== void 0 ? _a : {
            typicalOnTime: 10000,
            typicalOffTime: 40000,
        };
        const dutyCycle = settings.typicalOnTime /
            (settings.typicalOnTime + settings.typicalOffTime);
        return new SometimesOnDeviceClient((0, uuid_1.v4)(), deviceClass, targetPower / deviceCount / dutyCycle, settings.typicalOnTime, settings.typicalOffTime, i === realMicrowaveIndex ? 500 : 4000, i !== realMicrowaveIndex, i === realMicrowaveIndex, (powered) => {
            if (i === realMicrowaveIndex) {
                // TODO this merges events from multiple microwaves together
                onMicrowaveChange(powered);
            }
        });
    });
    return { deviceClients, realMicrowaveIndex };
}
exports.createVirtualDevices = createVirtualDevices;
